import { c as _c } from "react/compiler-runtime";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { Icon } from "@/components/formatters/MXPIcon";
import { cardSrc, CARD_SOURCES } from "@/lib/heroImageUtils";
import Image from "next/image";
import Tooltip from "../../component/Tooltip/Tooltip";

/**
 * One capability rendered on a {@link FilterableCard}. Mirrors the
 * `productCapability` content type queried by `filterableCardsQuery.ts`.
 */
export interface ProductCapability {
  title: string;
  icon?: string;
  description?: string;
}
export interface FilterableCardProps {
  image?: {
    url: string;
    alt: string;
    height?: number;
    width?: number;
  };
  eyebrow?: string;
  header?: string;
  subHeader?: string;
  button?: {
    label: string;
    destination: string;
  };
  /**
   * Optional secondary CTA rendered next to {@link FilterableCardProps.button}
   * with non-primary styling. Sourced from Contentful `filterableCard.secondaryCta`.
   */
  secondaryButton?: {
    label: string;
    destination: string;
  };
  /**
   * When true, the card renders a small "Featured" badge in the top-right
   * corner. Sourced from Contentful `filterableCard.featuredCard`.
   */
  featured?: boolean;
  /**
   * Capability bullets shown between the description and the CTA row.
   * Sourced from Contentful `filterableCard.productCapabilities`.
   */
  productCapabilities?: ProductCapability[];
  /**
   * Promotional chips rendered above the capability row (e.g. "Free Trial",
   * "New", "Limited Time"). Sourced from Contentful `filterableCard.offers`
   * (Array<Link → mxpTag>, max 2). The `label` is the referenced tag's
   * `displayName`. Styling matches the ecomm discount badge in
   * {@link CardEcommerce} so promotional visual language stays consistent
   * across the site.
   */
  offers?: {
    label: string;
  }[];
  truncate?: boolean;
  truncateLength?: number;
  /** Localized "Featured" badge text. Defaults to English when omitted. */
  featuredLabel?: string;
  /** Localized aria-label for the featured badge. */
  featuredCardAriaLabel?: string;
}
export const FilterableCard = t0 => {
  const $ = _c(57);
  const {
    image,
    eyebrow,
    header,
    subHeader,
    button,
    secondaryButton,
    featured,
    productCapabilities,
    offers,
    truncate,
    truncateLength,
    featuredLabel: t1,
    featuredCardAriaLabel: t2
  } = t0;
  const featuredLabel = t1 === undefined ? "Featured" : t1;
  const featuredCardAriaLabel = t2 === undefined ? "Featured card" : t2;
  const hasCapabilities = Array.isArray(productCapabilities) && productCapabilities.length > 0;
  let hasAnyButton;
  let renderIcon;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== button?.label || $[1] !== eyebrow || $[2] !== featured || $[3] !== featuredCardAriaLabel || $[4] !== featuredLabel || $[5] !== header || $[6] !== image || $[7] !== offers || $[8] !== secondaryButton?.label || $[9] !== subHeader || $[10] !== truncate || $[11] !== truncateLength) {
    const visibleOffers = Array.isArray(offers) ? offers.filter(_temp) : [];
    const hasOffers = visibleOffers.length > 0;
    hasAnyButton = Boolean(button?.label || secondaryButton?.label);
    renderIcon = _temp2;
    t8 = `t2-c t2-card--filterable bg-white shadow-card w-full h-full flex flex-col rounded-[10px] overflow-hidden t2-color-setting--light relative${featured ? " ring-2 ring-blue-trmb" : ""}`;
    if ($[22] !== featured || $[23] !== featuredCardAriaLabel || $[24] !== featuredLabel) {
      t9 = featured && <span className="absolute top-0 right-0 left-0 z-1 bg-blue-trmb text-white font-bold uppercase tracking-widest px-2 py-1 rounded-t-[8px] text-center" aria-label={featuredCardAriaLabel}>{featuredLabel}</span>;
      $[22] = featured;
      $[23] = featuredCardAriaLabel;
      $[24] = featuredLabel;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    if ($[26] !== image) {
      t10 = image?.url && <div className="w-full bg-cover bg-center text-transparent select-none rounded-t-[10px]"><Image src={image.url} alt={image.alt || ""} width={CARD_SOURCES.bound.w} height={CARD_SOURCES.bound.h} className="w-full h-full object-cover" /></div>;
      $[26] = image;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t3 = "flex-grow pt-8 px-8 pb-0";
    if ($[28] !== eyebrow) {
      t4 = eyebrow && <div className="text-blue-trmb uppercase text-sm font-bold leading-5 tracking-wider pb-2">{eyebrow}</div>;
      $[28] = eyebrow;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    if ($[30] !== header) {
      t5 = header && <h3 className="text-gray-m9 text-3xl font-thin leading-[1.2] break-words m-0 mb-3">{header}</h3>;
      $[30] = header;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    if ($[32] !== subHeader || $[33] !== truncate || $[34] !== truncateLength) {
      t6 = subHeader && <CommonDescription description={subHeader} className="text-gray-m9 text-base leading-relaxed font-normal mb-6" truncate={truncate} truncateLength={truncateLength} />;
      $[32] = subHeader;
      $[33] = truncate;
      $[34] = truncateLength;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    t7 = hasOffers && <ul className="flex flex-row flex-wrap gap-2 mb-6 list-none p-0 m-0">{visibleOffers.map(_temp3)}</ul>;
    $[0] = button?.label;
    $[1] = eyebrow;
    $[2] = featured;
    $[3] = featuredCardAriaLabel;
    $[4] = featuredLabel;
    $[5] = header;
    $[6] = image;
    $[7] = offers;
    $[8] = secondaryButton?.label;
    $[9] = subHeader;
    $[10] = truncate;
    $[11] = truncateLength;
    $[12] = hasAnyButton;
    $[13] = renderIcon;
    $[14] = t10;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    hasAnyButton = $[12];
    renderIcon = $[13];
    t10 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[36] !== hasCapabilities || $[37] !== productCapabilities || $[38] !== renderIcon) {
    t11 = hasCapabilities && <ul className="flex flex-row gap-3 mb-6">{productCapabilities.map((capability, i_0) => <li key={`capability-${i_0}-${capability.title}`} className="flex items-start gap-3 overflow-visible">{capability?.icon && renderIcon(capability?.icon, capability?.description || "")}</li>)}</ul>;
    $[36] = hasCapabilities;
    $[37] = productCapabilities;
    $[38] = renderIcon;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t11 || $[41] !== t3 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7) {
    t12 = <div className={t3}>{t4}{t5}{t6}{t7}{t11}</div>;
    $[40] = t11;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== button || $[48] !== hasAnyButton || $[49] !== secondaryButton) {
    t13 = hasAnyButton && <div className="px-8 pb-8 mt-auto flex flex-wrap gap-x-4 gap-y-5 flex-col justify-between">{button?.label && <TerraLink primary={true} label={button.label} destination={button.destination} size="small" className="w-full flex" />}{secondaryButton?.label ? <TerraLink linkType="text" primary={false} label={secondaryButton.label} destination={secondaryButton.destination} size="medium" className="text-blue-trmb font-bold leading-5 text-center items-center justify-center" /> : <span className="t2-terra-text-link inline-flex items-center justify-center font-bold leading-5 text-center invisible select-none" aria-hidden={true}> </span>}</div>;
    $[47] = button;
    $[48] = hasAnyButton;
    $[49] = secondaryButton;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t10 || $[52] !== t12 || $[53] !== t13 || $[54] !== t8 || $[55] !== t9) {
    t14 = <div className={t8}>{t9}{t10}{t12}{t13}</div>;
    $[51] = t10;
    $[52] = t12;
    $[53] = t13;
    $[54] = t8;
    $[55] = t9;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  return t14;
};
function _temp(o) {
  return o?.label?.trim();
}
function _temp2(icon, tooltipText) {
  const formattedIcon = `mxp-icon-${icon}`;
  if (tooltipText) {
    return <Tooltip tooltipIcon={formattedIcon} tooltipLabel={tooltipText} direction="right" iconSize={32}><p>{tooltipText}</p></Tooltip>;
  } else {
    return <span className="shrink-0 w-8 h-8 flex items-center justify-center text-blue-trmb" aria-hidden={true}><Icon type={formattedIcon as never} size={32} /></span>;
  }
}
function _temp3(offer, i) {
  return <li key={`offer-${i}-${offer.label}`}><span className="inline-block px-2 py-1 bg-green-m2 text-green-m15 uppercase rounded-md text-xs font-extrabold leading-4 tracking-wide">{offer.label}</span></li>;
}